import React, { useContext, useLayoutEffect, useState } from 'react'
import { graphql, Link } from 'gatsby';
import { css } from '@emotion/react';
import { ThemeContext } from '../components/theme-provider';
import SEO from '../components/seo';
import FullImage from '../components/full-image';
import PortableText from '../components/portable-text';
import ArtistScroller from '../components/artist-scroller';
import Accordion from '../components/accordion';

export default function Artists({ data }) {
  const [artists, setArtists] = useState([]);
  const [year, setYear] = useState(null);
  const [years, setYears] = useState([]);
  const [yearOpen, setYearOpen] = useState(false);
  const [search, setSearch] = useState('');

  const theme = useContext(ThemeContext);

  useLayoutEffect(() => {
    theme.setGrayFooter(false);
  }, []);

  useLayoutEffect(() => {
    const people = [...data.people.nodes];
    const programs = [...data.programs.nodes];
    const newArtists = [];

    people
      .map((person) => {
        const activeYears = programs
          .filter((program) => program.people.some((el) => el._id === person._id))
          .map((program) => new Date(program.startDate).getFullYear());

        return {
          title: person.name,
          years: activeYears,
          slug: person.slug?.current,
        };
      })
      .sort((a, b) => (a.title < b.title) ? -1 : 1)
      .filter((person) => person.years.some((el) => {
        if (year) {
          return el === year;
        } else {
          return true;
        }
      }))
      .filter((person) => {
        const lowerSearch = search.toLowerCase();

        if (person.years.map((year) => year).join(' ').toLowerCase().includes(lowerSearch)) return true;
        if (person.title.toLowerCase().includes(lowerSearch)) return true;
      })
      .forEach((person) => {
        if (!newArtists[person.title[0]]) newArtists[person.title[0]] = [];
        newArtists[person.title[0]].push(person)
      });

    setArtists(newArtists);

    const newYears = data.programs.nodes
      .map((program) => new Date(program.startDate).getFullYear())
      .filter((el, i, array) => array.indexOf(el) === i)
      .sort((a, b) => (a > b) ? -1 : 1);

    setYears(newYears);
  }, [data.people.nodes, data.programs.nodes, year, search]);

  return (
    <div
      className="type--45"
      css={css`
        background-color: var(--white);
      `}
    >
      <SEO
        title="Artists"
        description={(data.artists._rawContent) ? data.artists._rawContent.map(el => el.children).flat().map((el) => el?.text)[0] : null}
        imageID={(data.artists.featureImage) ? data.artists.featureImage.asset._id : null}
      />
      <FullImage
        text={`
          Artists/
        `}
        includeTitle
        image={data.artists.featureImage}
      />
      <div
        css={css`
          padding: 0 var(--margin);
          margin-bottom: var(--bigMargin);
        `}
      >
        <PortableText
          blocks={data.artists._rawContent}
        />
      </div>
      <div
        css={css`
          padding: var(--bigMargin) 0;
        `}
      >
        {data.artists.residentCompanies.length > 0 && <ArtistScroller
          title=<div>Artists/<br />Resident Companies/</div>
          artists={data.artists.residentCompanies}
        />}
        {data.artists.inResidence.length > 0 && <ArtistScroller
          title=<div>Artists/<br />Residents/</div>
          artists={data.artists.inResidence}
        />}
        {data.artists.inDevelopment.length > 0 && <ArtistScroller
          title=<div>Artists/<br />In Development/</div>
          artists={data.artists.inDevelopment}
        />}
      </div>
      <div
        css={css`
          color: var(--white);
          background-image: linear-gradient(to bottom, transparent, var(--black) 15rem, var(--black) calc(100% - 15rem), transparent);
          text-transform: uppercase;
          padding: 20rem var(--margin);
        `}
      >
        <PortableText
          css={css`
            text-transform: none;
            margin-bottom: var(--bigMargin);
          `}
          blocks={data.artists?._rawListText}
        />
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(12, minmax(0, 1fr));
            gap: var(--gutter);

            [role="button"] {
              cursor: pointer;
              transition: color 0.6s var(--curve);

              &:hover {
                color: var(--gray);
                transition: color 0s;
              }
            }
          `}
        >
          <Accordion
            css={css`
              grid-column: span 2;
              color: var(--black);
              background-color: var(--white);
              padding: 0 calc(var(--gutter) * 0.75);
              margin-bottom: var(--margin);

              @media (max-width: 1080px) {
                grid-column: span 6;
              }

              @media (max-width: 700px) {
                grid-column: span 12;
                padding: 0.8rem calc(var(--gutter) * 0.75);
                margin-bottom: 1rem;
              }
            `}
            title={(year) ? year : 'All'}
            open={yearOpen}
            setOpen={setYearOpen}
            small
            noPadding
          >
            <div
              css={css`
              `}
              role="button"
              onClick={() => { setYear(null); setYearOpen(false); }}
            >
              All
            </div>
            {years.length > 0 && years
              .map((year, i) => (
                <div
                  key={i}
                  css={css`
                  `}
                  role="button"
                  onClick={() => { setYear(year); setYearOpen(false); }}
                >
                  {year}
                </div>
              ))
            }
          </Accordion>
          <div
            css={css`
              position: relative;
              grid-column-start: 7;
              grid-column-end: span 6;
              height: 6.2rem;
              color: var(--black);
              background-color: var(--white);
              padding: 0 calc(var(--gutter) * 0.75);
              margin-bottom: var(--margin);

              @media (max-width: 700px) {
                grid-column-start: 1;
                grid-column-end: span 12;
              }
            `}
          >
            <input
              css={css`
                position: relative;
                top: 0.9rem;
                width: 100%;
                height: 5.3rem;
                padding-right: 3rem;
                border: 0;
              `}
              placeholder="Search..."
              onKeyUp={(e) => {
                e.persist();
               
                setSearch(e.target.value);
              }}
            />
            <img
              css={css`
                position: absolute;
                top: calc(var(--gutter) * 1);
                right: var(--gutter);
                width: auto;
                height: 3rem;
                z-index: 1;
              `}
              src="/images/glass.svg"
            />
          </div>
        </div>
        <div
          css={css`
            columns: 2;
            column-gap: var(--gutter);
            column-fill: balance-all;
            padding-top: var(--margin);
            border-top: 2px solid var(--white);

            @media (max-width: 700px) {
              columns: 1;
            }
            
            a {
              color: var(--white);
              text-decoration: none;
              transition: color 0.6s var(--curve);

              &:hover {
                color: var(--primary);
                transition: color 0s;
              }
            }
          `}
        >
          {Object.keys(artists).length > 0 && Object.keys(artists)
            .map((letter, i) => (
              <div
                key={i}
                css={css`
                  break-inside: avoid-column;
                  margin-bottom: var(--margin);
                `}
              >
                <div>{letter}</div>
                {artists[letter].length > 0 && artists[letter]
                  .map((artist, i) => (
                    <div
                      key={i}
                    >
                      <Link
                        to={`/artist/${artist.slug}`}
                      >{artist.title}</Link>
                    </div>
                  ))
                }
              </div>
            ))
          }
          <div
            css={css`
              height: 5em;
            `}
          />
        </div>
      </div>
    </div>
  );
}

export const query = graphql`
  query {
    artists: sanityArtists(_id: { regex: "/(drafts\\\\.)?artists/" }) {
      id
      featureImage {
        ...Image
        asset {
          description
        }
      }
      _rawContent(resolveReferences: {maxDepth: 10})
      residentCompanies {
        slug {
          current
        }
        featureImage {
          ...Image
        }
        type
        name
      }
      inResidence {
        slug {
          current
        }
        featureImage {
          ...Image
        }
        type
        name
      }
      inDevelopment {
        slug {
          current
        }
        featureImage {
          ...Image
        }
        type
        name
      }
      _rawListText(resolveReferences: {maxDepth: 10})
    }
    people: allSanityPerson {
      nodes {
        _id
        name
        slug {
          current
        }
      }
    }
    programs: allSanityProgram(sort: {fields: startDate, order: DESC}, filter: {private: {ne: true}}) {
      nodes {
        startDate
        people {
          _id
        }
      }
    }
  }
`

